import React, { useEffect, useState } from 'react';
import { HiOutlineShieldCheck, HiOutlineClock, HiOutlineExclamationTriangle, HiOutlineCheckCircle } from 'react-icons/hi2';
import { workPermitService } from '../services';
import plantService from '../services/plantService';
import PageLoading from '../components/common/PageLoading';
import './WorkPermitDashboard.css';

const WorkPermitDashboard = () => {
  const [stats, setStats] = useState({ total: 0, active: 0, expiring: 0, closed: 0 });
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [plants, setPlants] = useState([]);
  const [selectedPlantId, setSelectedPlantId] = useState("");

  const handlePlantChange = (val) => {
    setSelectedPlantId(val);
    if (val) {
        localStorage.setItem('defaultPlantId', val);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [permits, expiringData, plantsData] = await Promise.all([
        workPermitService.getWorkPermits({ plantId: selectedPlantId }),
        workPermitService.getExpiringPermits(24, selectedPlantId),
        plantService.getAllPlants()
      ]);

      setPlants(plantsData || []);

      // Auto-select plant if only 1 exists
      if (plantsData?.length === 1) {
          setSelectedPlantId(plantsData[0].plantId.toString());
      }

      const activeStatuses = ['Active', 'Issued', 'Work in Progress'];
      const closedStatuses = ['Closed', 'Cancelled'];
      setStats({
        total: permits?.length || 0,
        active: permits?.filter((p) => activeStatuses.includes(p.status)).length || 0,
        expiring: expiringData?.length || 0,
        closed: permits?.filter((p) => closedStatuses.includes(p.status)).length || 0,
      });
      setExpiring(expiringData || []);
    } catch (err) {
      console.error('Error loading work permit dashboard:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load permit dashboard. Please try again.';
      setError(errorMessage);
      setStats({ total: 0, active: 0, expiring: 0, closed: 0 });
      setExpiring([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedPlantId]);

  return (
    <div className="pagecontainer wp-dashboard-page">
      <div className="page-header">
        <div>
          <h1>Work Permit Dashboard</h1>
          <p>Overview of permit volume, activity, and expiring items.</p>
        </div>
        <div className="header-actions">
           <select 
             className="plant-select"
             value={selectedPlantId}
             onChange={(e) => handlePlantChange(e.target.value)}
           >
             <option value="">All Plants</option>
             {plants.map(p => (
               <option key={p.plantId} value={p.plantId}>{p.plantName}</option>
             ))}
           </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      
      <div className="wp-stats-grid">
        <div className="wp-stat-card">
          <div className="wp-stat-icon primary"><HiOutlineShieldCheck /></div>
          <div>
            <div className="label">Total Permits</div>
            {loading ? (
              <div className="value loading-skeleton">-</div>
            ) : (
              <div className="value">{stats.total}</div>
            )}
          </div>
        </div>
        <div className="wp-stat-card">
          <div className="wp-stat-icon success"><HiOutlineCheckCircle /></div>
          <div>
            <div className="label">Active / WIP</div>
            {loading ? (
              <div className="value loading-skeleton">-</div>
            ) : (
              <div className="value">{stats.active}</div>
            )}
          </div>
        </div>
        <div className="wp-stat-card">
          <div className="wp-stat-icon warning"><HiOutlineExclamationTriangle /></div>
          <div>
            <div className="label">Expiring (24h)</div>
            {loading ? (
              <div className="value loading-skeleton">-</div>
            ) : (
              <div className="value">{stats.expiring}</div>
            )}
          </div>
        </div>
        <div className="wp-stat-card">
          <div className="wp-stat-icon neutral"><HiOutlineClock /></div>
          <div>
            <div className="label">Closed / Cancelled</div>
            {loading ? (
              <div className="value loading-skeleton">-</div>
            ) : (
              <div className="value">{stats.closed}</div>
            )}
          </div>
        </div>
      </div>

      <div className="table-card">
        <div className="table-head">
          <span>Expiring in next 24h</span>
          {loading ? (
            <PageLoading variant="muted" message="Loading…" className="wp-dash-inline-hint" />
          ) : (
            <span className="hint">{expiring.length} item(s)</span>
          )}
        </div>
        {loading ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading expiring permits…" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="wp-table">
              <thead>
                <tr>
                  <th>Permit #</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Equipment</th>
                  <th>Valid To</th>
                </tr>
              </thead>
              <tbody>
                {expiring.length === 0 && (
                  <tr>
                    <td colSpan="5" className="empty-state">No permits expiring soon.</td>
                  </tr>
                )}
                {expiring.map((p) => (
                  <tr key={p.permitId}>
                    <td>{p.permitNumber}</td>
                    <td>{p.permitTypeName || '-'}</td>
                    <td>{p.status}</td>
                    <td>{p.equipmentName || '-'}</td>
                    <td>{p.validTo ? new Date(p.validTo).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkPermitDashboard;

